'use client'

import { sendGenericEvent } from 'analytics/events/generics/sendGenericEvent'
import { useFirstScroll } from 'plp/hooks/useFirstScroll'
import { useScrollToBottom } from 'plp/hooks/useScrollToBottom'
import {
	type AppliedFiltersIn,
	PlpActionEvents,
	PlpGenericEvents,
	type TopNavigationArgs,
} from 'plp/types/Analytics'
import type { ApplyArgs } from 'plp/types/Filters'
import { EventName } from 'plp/types/eventsObserver'
import { eventsObserver } from 'plp/utils/eventsObserver'
import { useEffect } from 'react'
import { debounce } from 'utils/debounce'

import { usePlpContext } from '../../context'
import { filtersToAnalytics } from './filtersToAnalytics'

const NO_RESULTS_DEBOUNCE_DELAY = 1000

interface AnalyticsProps {
	filtered: boolean
}

export const useAnalytics = ({ filtered }: AnalyticsProps) => {
	const { columns } = usePlpContext()
	useFirstScroll()
	useScrollToBottom()

	// TODO deleteTagFilters: Tag · Falta onSale, price
	// TODO sendApplyFiltersEvent · Falta test case en .spec.tsx
	// TODO sendPlpEnd se envía bien sin aplicar filtros, pero con filtros activados no se envía

	const sendOpenFiltersEvent = (): void => {
		const payload = {
			columns_number: columns,
			filtered,
		}
		sendGenericEvent(PlpGenericEvents.OPEN_FILTERS, payload)
	}

	const sendApplyFiltersEvent = (appliedFilters: AppliedFiltersIn): void => {
		const payload = filtersToAnalytics(appliedFilters)
		sendGenericEvent(PlpGenericEvents.APPLY_FILTERS, payload)
	}

	const sendCloseFiltersEvent = (): void => {
		const payload = {
			columns_number: columns,
			filtered,
		}
		sendGenericEvent(PlpGenericEvents.CLOSE_FILTERS, payload)
	}

	const sendDeleteTagFiltersEvent = (applyArgs: ApplyArgs): void => {
		const payload = {
			tag: applyArgs.deletedType,
		}
		sendGenericEvent(PlpGenericEvents.DELETE_TAG_FILTERS, payload)
	}

	const sendPlpActionEvent = (label1: string): void => {
		const payload = {
			label_1: label1,
			columns_number: columns,
			filtered,
		}
		sendGenericEvent(PlpGenericEvents.PLP_ACTION, payload)
	}

	const sendNoResultFiltersEvent = (): void => {
		sendGenericEvent(PlpGenericEvents.NO_RESULTS_FILTERS)
	}

	const sendDeleteFiltersEvent = (): void => {
		sendGenericEvent(PlpGenericEvents.DELETE_FILTERS)
	}

	const sendPlpEndEvent = (): void => {
		const payload = {
			columns_number: columns,
			filtered,
		}
		sendGenericEvent(PlpGenericEvents.PLP_END, payload)
	}

	const debouncedSendNoResultFiltersEvent = debounce(
		sendNoResultFiltersEvent,
		NO_RESULTS_DEBOUNCE_DELAY
	)

	const sendTopNavigationEvent = ({
		subfamilies,
		type,
	}: TopNavigationArgs): void => {
		const payload = {
			subfamilies,
			type,
		}
		sendGenericEvent(PlpGenericEvents.TOP_NAVIGATION, payload)
	}

	useEffect(() => {
		const openFiltersHandler = () => sendOpenFiltersEvent()
		const closeFiltersHandler = () => sendCloseFiltersEvent()
		const noResultFiltersHandler = () => debouncedSendNoResultFiltersEvent()
		const deleteFiltersHandler = () => sendDeleteFiltersEvent()
		const applyFiltersHandler = (appliedFilters: AppliedFiltersIn) =>
			sendApplyFiltersEvent(appliedFilters)
		const deleteTagFiltersHandler = (applyArgs: ApplyArgs) =>
			sendDeleteTagFiltersEvent(applyArgs)
		const plpEndHandler = () => sendPlpEndEvent()
		const changeColumnsHandler = () =>
			sendPlpActionEvent(PlpActionEvents.CHANGE_COLUMNS)
		const firstScrollHandler = () => sendPlpActionEvent(PlpActionEvents.SCROLL)
		const topNavigationHandler = (topNavigationArgs: TopNavigationArgs) =>
			sendTopNavigationEvent(topNavigationArgs)

		eventsObserver.subscribe(EventName.OPEN_FILTERS, openFiltersHandler)
		eventsObserver.subscribe(EventName.CLOSE_FILTERS, closeFiltersHandler)
		eventsObserver.subscribe(EventName.ZERO_RESULTS, noResultFiltersHandler)
		eventsObserver.subscribe(EventName.CLEAR_FILTERS, deleteFiltersHandler)
		eventsObserver.subscribe(EventName.APPLY_FILTERS, applyFiltersHandler)
		eventsObserver.subscribe(EventName.DELETE_TAG, deleteTagFiltersHandler)
		eventsObserver.subscribe(EventName.PLP_END, plpEndHandler)
		eventsObserver.subscribe(EventName.CHANGE_COLUMNS, changeColumnsHandler)
		eventsObserver.subscribe(EventName.FIRST_SCROLL, firstScrollHandler)
		eventsObserver.subscribe(EventName.TOP_NAVIGATION, topNavigationHandler)

		return () => {
			eventsObserver.unsubscribe(openFiltersHandler)
			eventsObserver.unsubscribe(closeFiltersHandler)
			eventsObserver.unsubscribe(noResultFiltersHandler)
			eventsObserver.unsubscribe(deleteFiltersHandler)
			eventsObserver.unsubscribe(applyFiltersHandler)
			eventsObserver.unsubscribe(deleteTagFiltersHandler)
			eventsObserver.unsubscribe(plpEndHandler)
			eventsObserver.unsubscribe(changeColumnsHandler)
			eventsObserver.unsubscribe(firstScrollHandler)
			eventsObserver.unsubscribe(topNavigationHandler)
		}
	}, [columns, filtered])
}
